import {
  type BackgroundMapCustomColorOptions,
  backgroundMapCustomStyleDefaults,
  isCustomBackgroundMapStyle,
} from "@/constants/backgroundMapCustomStyle";
import {
  Language,
  MaptilerBorderId,
  MaptilerEuropeFIRId,
  onlyBorderStyleLayer,
} from "@/constants/maptilerStyleOptions";
import { FormCheckbox } from "@/overlay/components/SmartForm/HookInputs/Checkbox";
import type { ParamOptionInputData } from "@/overlay/components/SmartForm/HookInputs/ParamOptionSelectList";
import { SelectInput } from "@/overlay/components/SmartForm/HookInputs/SelectInput";
import { SliderInput } from "@/overlay/components/SmartForm/HookInputs/SliderInput";
import { ModeSwitchableForm } from "@/overlay/components/SmartForm/SmartFormUtils/FormComponents/ModeSwitchableFormProps";
import { useFormWithUtils } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormUtils";
import type { BaseFormProps } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { t } from "@lingui/core/macro";
import type { BackgroundMapLayer, BackgroundMapLayerCreate } from "@mm/metx-workbench.meteomatics.com";
import { CartographicMaterialColorPicker } from "./CartographicMaterialColorPicker";
import { useLanguageOptions, useMaptilerStyleOptions } from "./hooks";

type BackgroundMapLayerPatch = Partial<BackgroundMapLayerCreate | BackgroundMapLayer>;

/**
 * Form data passed from the child components.
 */
export type CartographicMaterialUpdateFormData = {
  // Snake case as they get passed as layer props directly.
  layerData: {
    style: string;
    custom_options: {
      map_label_language?: string;
      show_state_border?: boolean;
      line_color?: string;
      custom_background_color?: string;
      custom_water_color?: string;
      custom_border_color?: string;
    };
    opacity: number;
  };
  // Form internal data to construct the above data.
  internal: {
    paramOptionPatch: ParamOptionInputData;
  };
};

type CartographicMaterialLayerUpdateFormProps = {
  defaultFormData: BackgroundMapLayerPatch;
} & BaseFormProps<CartographicMaterialUpdateFormData>;

/**
 * Responsible for rendering the whole layer form and send back the data on submit hadler.
 */
export function CartographicMaterialUpdateForm({
  submitMode,
  onSubmit,
  onCancel,
  defaultFormData,
}: CartographicMaterialLayerUpdateFormProps) {
  const { formMethods, formValName } = useFormWithUtils<CartographicMaterialUpdateFormData>({
    mode: submitMode,
  });
  const { layerData } = formMethods.watch();
  const customColorDefaults = defaultFormData.custom_options as BackgroundMapCustomColorOptions | undefined;
  const selectedStyle = layerData?.style ?? defaultFormData.style ?? "";
  const isCustomStyleSelected = isCustomBackgroundMapStyle(selectedStyle);

  const onSubmitWithFormatting: typeof onSubmit = (formData) => {
    onSubmit(formData);
  };
  const submitCurrentForm = () => {
    void formMethods.handleSubmit(onSubmitWithFormatting)();
  };

  const languageOptions = useLanguageOptions();
  const styleOptions = useMaptilerStyleOptions();

  return (
    <ModeSwitchableForm
      onSubmit={formMethods.handleSubmit(onSubmitWithFormatting)}
      onCancel={onCancel}
      submitMode={submitMode}
    >
      <SelectInput
        label={t`Basemap`}
        values={styleOptions}
        instruction={{
          currValue: defaultFormData.style,
          formValueName: formValName("layerData.style"),
          formMethods,
        }}
      />

      {/* For some reason values get's populated only when select is open first time */}
      {!onlyBorderStyleLayer.includes(selectedStyle) && (
        <SelectInput
          label={t`Map Label Language`}
          values={languageOptions}
          instruction={{
            currValue: defaultFormData.custom_options?.map_label_language ?? Language.EN,
            formValueName: formValName("layerData.custom_options.map_label_language"),
            formMethods,
          }}
        />
      )}
      <SliderInput
        label={t`Opacity`}
        range={{ min: 0, max: 1, step: 0.1 }}
        instruction={{
          currValue: defaultFormData.opacity ?? 1,
          formValueName: formValName("layerData.opacity"),
          formMethods,
        }}
      />
      {selectedStyle === MaptilerBorderId && (
        <FormCheckbox
          label={t`show state border`}
          instruction={{
            currValue: !!defaultFormData.custom_options?.show_state_border,
            formValueName: formValName("layerData.custom_options.show_state_border"),
            formMethods,
          }}
        />
      )}
      {(selectedStyle === MaptilerBorderId || selectedStyle === MaptilerEuropeFIRId) && (
        <CartographicMaterialColorPicker
          label={t`Line Color`}
          onValueCommit={submitCurrentForm}
          instruction={{
            currValue: defaultFormData.custom_options?.line_color || "black",
            formValueName: formValName("layerData.custom_options.line_color"),
            formMethods,
          }}
        />
      )}
      {isCustomStyleSelected && (
        <>
          <CartographicMaterialColorPicker
            label={t`Background Color`}
            onValueCommit={submitCurrentForm}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_background_color ??
                customColorDefaults?.custom_background_color ??
                backgroundMapCustomStyleDefaults.backgroundColor,
              formValueName: formValName("layerData.custom_options.custom_background_color"),
              formMethods,
            }}
          />
          <CartographicMaterialColorPicker
            label={t`Water Color`}
            onValueCommit={submitCurrentForm}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_water_color ??
                customColorDefaults?.custom_water_color ??
                backgroundMapCustomStyleDefaults.waterColor,
              formValueName: formValName("layerData.custom_options.custom_water_color"),
              formMethods,
            }}
          />
          <CartographicMaterialColorPicker
            label={t`Border Color`}
            onValueCommit={submitCurrentForm}
            instruction={{
              currValue:
                layerData?.custom_options?.custom_border_color ??
                customColorDefaults?.custom_border_color ??
                backgroundMapCustomStyleDefaults.borderColor,
              formValueName: formValName("layerData.custom_options.custom_border_color"),
              formMethods,
            }}
          />
        </>
      )}
    </ModeSwitchableForm>
  );
}
